import { BuildingUserConstants } from './types';
import { NetworkService } from '../../services';
import { config } from '../../config';

// API endpoints
const { BASE_URL, BUILDING_USERS_URL } = config;
const GET_BUILDING_USERS = `${BASE_URL}${BUILDING_USERS_URL}invites/`;
const CREATE_USER_INVITE = `${BASE_URL}${BUILDING_USERS_URL}invite/`;
const VALIDATE_USER_INVITE = `${BASE_URL}${BUILDING_USERS_URL}validate-invite/`;
const GET_SCOPES_LIST = `${BASE_URL}${BUILDING_USERS_URL}scopes/`;

export class BuildingUserActions {
  static getBuildingUsers(searchValue = '', sortValue = '', isReversed, usersPerPage, currentPage) {
    return async (dispatch) => {
      dispatch({ type: BuildingUserConstants.StartLoading });

      const requestOptions = NetworkService.getRequestOptions('GET');

      const API_ENDPOINT =
        GET_BUILDING_USERS +
        `?ordering=${
          isReversed ? '-' : ''
        }${sortValue}&page_size=${usersPerPage}&page=${currentPage}&email=${searchValue}`;

      const response = await fetch(API_ENDPOINT, requestOptions);

      const data = await response.json();

      if (!response || !response.ok) {
        const { message } = data;

        return dispatch(BuildingUserActions.setBuildingUserError(message));
      }

      return dispatch({ type: BuildingUserConstants.SetBuildingUsers, payload: data });
    };
  }

  static createUserInvite(email, building, userType, scopes = []) {
    return async (dispatch) => {
      dispatch({ type: BuildingUserConstants.StartLoading });

      const requestOptions = NetworkService.getRequestOptions(
        'POST',
        JSON.stringify({
          email: email,
          scopes: scopes,
          user_role: userType,
          building_identifier: building,
        }),
      );

      const response = await fetch(`${CREATE_USER_INVITE}`, requestOptions).catch(() => 'error');
      const data = await response.json();

      if (!response || !response.ok || response === 'error') {
        if (response.status === 400) {
          const errorMessage =
            'The invite creation failed. Please return, try again, and double check the input information. (You may have tried to add a duplicate user for the same building)';

          return dispatch(BuildingUserActions.setBuildingUserError(errorMessage));
        }

        const { message } = data.errors[0];

        return dispatch(BuildingUserActions.setBuildingUserError(message));
      }

      return dispatch({ type: BuildingUserConstants.CreateUserInvite });
    };
  }

  static validateUserInvite(token) {
    return async (dispatch) => {
      dispatch({ type: BuildingUserConstants.StartLoading });

      const callback = () => {
        const requestOptions = NetworkService.getRequestOptions(
          'POST',
          JSON.stringify({
            invite_token: token,
          }),
        );

        return fetch(VALIDATE_USER_INVITE, requestOptions);
      };

      const response = await NetworkService.fetchWithRetry(dispatch, callback);
      const data = await response.json();

      if (!response || !response.ok || response === 'error') {
        const message =
          'There was a problem validating your token. It may have expired or become corrupted. Please contact your inviter and ask them to resend the invite.';

        return dispatch(BuildingUserActions.setBuildingUserError(message));
      }

      return dispatch({ type: BuildingUserConstants.ReceiveValidInvite, payload: data });
    };
  }

  static acceptNewUserInvite(API_LOCATION, firstName, lastName, email, phone, password1, password2) {
    return async (dispatch) => {
      const requestOptions = NetworkService.getRequestOptions(
        'POST',
        JSON.stringify({
          last_name: lastName,
          first_name: firstName,
          phone_number: phone,
          password: password1,
          password_confirm: password2,
          email: email,
        }),
      );

      const response = await fetch(`${BASE_URL}${API_LOCATION}`, requestOptions).catch(() => 'error');

      if (!response || !response.ok || response === 'error') {
        const message = 'An error has occurred and we are unable to register your information.';

        return dispatch(BuildingUserActions.setBuildingUserError(message));
      }

      return dispatch({ type: BuildingUserConstants.InviteAccepted });
    };
  }

  static acceptInvite(API_LOCATION, email) {
    return async (dispatch) => {
      const requestOptions = NetworkService.getRequestOptions(
        'POST',
        JSON.stringify({
          email: email,
        }),
      );

      const response = await fetch(`${BASE_URL}${API_LOCATION}`, requestOptions).catch(() => 'error');

      if (!response || !response.ok || response === 'error') {
        const message = 'An error has occurred and we are unable to grant you access.';

        return dispatch(BuildingUserActions.setBuildingUserError(message));
      }

      return dispatch({ type: BuildingUserConstants.InviteAccepted });
    };
  }

  static getUserScopes() {
    return async (dispatch) => {
      dispatch({ type: BuildingUserConstants.StartLoading });

      const callback = () => {
        const requestOptions = NetworkService.getRequestOptions('GET');

        return fetch(GET_SCOPES_LIST, requestOptions);
      };

      const response = await NetworkService.fetchWithRetry(dispatch, callback);
      const data = await response.json();

      if (!response || !response.ok) {
        const { message } = data;

        return dispatch(BuildingUserActions.setBuildingUserError(message));
      }

      return dispatch({ type: BuildingUserConstants.SetUserScopes, payload: data });
    };
  }

  static executeUserAction(type, apiLocation, email) {
    return async (dispatch) => {
      switch (type) {
        case 'resendInvite':
          return dispatch(BuildingUserActions.resendInvite(apiLocation, email));
        case 'revokeAccess':
          return dispatch(BuildingUserActions.revokeInvite(apiLocation));
        case 'resetPassword':
          return dispatch(BuildingUserActions.resetPassword(apiLocation, email));
        default:
          return;
      }
    };
  }

  static resendInvite(apiLocation, email) {
    return async (dispatch) => {
      const callback = () => {
        const requestOptions = NetworkService.getRequestOptions(
          'POST',
          JSON.stringify({
            email: email,
          }),
        );

        return fetch(`${BASE_URL}${apiLocation}`, requestOptions);
      };

      const response = await NetworkService.fetchWithRetry(dispatch, callback);
      const data = await response.json();

      if (!response || !response.ok) {
        const { message } = data;

        return dispatch(BuildingUserActions.setBuildingUserError(message));
      }

      return dispatch({ type: BuildingUserConstants.SetUserActionSuccess, payload: 'Invite successfully resent!' });
    };
  }

  static revokeInvite(apiLocation) {
    return async (dispatch) => {
      const callback = () => {
        const requestOptions = NetworkService.getRequestOptions('DELETE');

        return fetch(`${BASE_URL}${apiLocation}`, requestOptions);
      };

      const response = await NetworkService.fetchWithRetry(dispatch, callback);

      if (!response || !response.ok) {
        return dispatch(
          BuildingUserActions.setBuildingUserError('There was an error revoking the invite, please try again.'),
        );
      }

      return dispatch({ type: BuildingUserConstants.SetUserActionSuccess, payload: 'Access successfully revoked!' });
    };
  }

  static resetPassword(apiLocation, email) {
    return async (dispatch) => {
      const callback = () => {
        const requestOptions = NetworkService.getRequestOptions(
          'POST',
          JSON.stringify({
            email: email,
          }),
        );

        return fetch(`${BASE_URL}${apiLocation}`, requestOptions);
      };

      const response = await NetworkService.fetchWithRetry(dispatch, callback);

      if (!response || !response.ok) {
        return dispatch(
          BuildingUserActions.setBuildingUserError(
            'There was an error trying to reset the password, please try again.',
          ),
        );
      }

      return dispatch({
        type: BuildingUserConstants.SetUserActionSuccess,
        payload: `Password reset email successfully sent to ${email}!`,
      });
    };
  }

  static revokeAccess(apiLocation, email) {
    return async (dispatch) => {
      const callback = () => {
        const requestOptions = NetworkService.getRequestOptions(
          'POST',
          JSON.stringify({
            email: email,
          }),
        );

        return fetch(`${BASE_URL}${apiLocation}`, requestOptions);
      };

      const response = await NetworkService.fetchWithRetry(dispatch, callback);
      const data = await response.json();

      if (!response || !response.ok) {
        const { message } = data;

        return dispatch(BuildingUserActions.setBuildingUserError(message));
      }
    };
  }

  static addUserPermission(apiLocation, permissionId) {
    return async (dispatch) => {
      const callback = () => {
        const requestOptions = NetworkService.getRequestOptions(
          'PATCH',
          JSON.stringify({
            identifier: permissionId,
          }),
        );

        return fetch(`${BASE_URL}${apiLocation}`, requestOptions);
      };

      const response = await NetworkService.fetchWithRetry(dispatch, callback);
      const data = await response.json();

      if (!response || !response.ok) {
        const { message } = data;

        return dispatch(BuildingUserActions.setBuildingUserError(message));
      }

      return dispatch({
        type: BuildingUserConstants.SetUserActionSuccess,
        payload: 'Permission successfully added!',
      });
    };
  }

  static removeUserPermission(apiLocation) {
    return async (dispatch) => {
      const callback = () => {
        const requestOptions = NetworkService.getRequestOptions('DELETE');

        return fetch(`${BASE_URL}${apiLocation}`, requestOptions);
      };

      const response = await NetworkService.fetchWithRetry(dispatch, callback);

      if (!response || !response.ok) {
        return dispatch(
          BuildingUserActions.setBuildingUserError(
            'There was an error trying to remove the permission, please try again.',
          ),
        );
      }

      return dispatch({
        type: BuildingUserConstants.SetUserActionSuccess,
        payload: 'Permission successfully removed!',
      });
    };
  }

  static setBuildingUserError(message) {
    return { type: BuildingUserConstants.SetBuildingUserError, payload: message };
  }

  static clearBuildingUserError() {
    return { type: BuildingUserConstants.ClearBuildingUserError };
  }

  static clearUserActionSuccess() {
    return { type: BuildingUserConstants.ClearUserActionSuccess };
  }
}
